import { useEffect, useRef, useState } from 'react';
import { recoverTerminalReturn, returnToMerchant, type TerminalReturn } from '../../services/TerminalReturnService';
import { hasNewTabResultSent, markNewTabResultSent, resolveFlowMode } from '../../utils/newTabHandler';
import { detectBrandFromHostname } from '../../utils/brand-urls';
import { getDevBrandSlug } from '../../contexts/DevBrandContext';
import { getLaunchCredential, getTerminalLaunchBinding } from '../../utils/launchCredentials';

/** A return screen, never active verification state. A sent marker means
 * attempted notification; it does not prove the merchant processed it. */
export function TerminalReturnScreen({ result }: { result: TerminalReturn }) {
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [attempted, setAttempted] = useState(() => hasNewTabResultSent(result.message.sessionId));
  const automatic = useRef(false);
  const binding = getTerminalLaunchBinding();
  const mobile = !!(getLaunchCredential('handoffToken') || getLaunchCredential('token') ||
    (binding?.sessionId === result.message.sessionId && (binding.params.handoffToken || binding.params.token)));
  const brand = detectBrandFromHostname(window.location.hostname) || getDevBrandSlug();
  const popup = resolveFlowMode() === 'new-tab' && !!window.opener && !mobile;

  useEffect(() => {
    if (automatic.current || !popup || attempted) return;
    automatic.current = true;
    // Persist BEFORE submission. Reload is ambiguous, so it never resends
    // automatically. An explicit retry below obtains a fresh terminal result.
    // Without a persistent marker, each reload would look like the first send.
    // Keep notification deliberate in that case, just like auto-navigation.
    if (!markNewTabResultSent(result.message.sessionId)) return;
    setAttempted(true);
    try { window.opener.postMessage(result.message, result.targetOrigin); } catch { setUnavailable(true); }
  }, [attempted, popup, result]);

  useEffect(() => {
    if (mobile || popup || new URLSearchParams(window.location.search).get('auto_return') !== 'true') return;
    const key = `terminal_return_attempt:${result.message.sessionId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, 'attempted');
      if (sessionStorage.getItem(key) !== 'attempted') return;
    } catch { return; } // Without a durable attempt marker, use the deliberate button.
    returnToMerchant(result); // Portal rechecks outcome and current destination.
  }, [mobile, popup, result]);

  const continueReturn = async (navigate = false) => {
    if (busy) return;
    setBusy(true);
    const fresh = await recoverTerminalReturn(result.message.sessionId, true);
    if (!fresh) { setUnavailable(true); setBusy(false); return; }
    if (popup && !navigate) {
      markNewTabResultSent(fresh.message.sessionId);
      try { window.opener.postMessage(fresh.message, fresh.targetOrigin); } catch { setUnavailable(true); }
      setAttempted(true);
      setBusy(false);
    } else returnToMerchant(fresh);
  };

  return <main className="app-container" style={{ padding: '2rem', textAlign: 'center' }}>
    {brand && <p>{brand === 'privateav' ? 'PrivateAV' : 'SafePassage'}</p>}
    <h1>{result.status === 'verified' ? 'Verification complete' : 'Verification finished'}</h1>
    <p>{mobile ? 'Return to your other device to continue.' : popup ? 'Return to the site where you started. If it has not continued, you can send the result again.' : 'Return to the site where you started to continue.'}</p>
    {unavailable && <p role="status">We could not confirm your result right now. Please try again.</p>}
    {popup && <button disabled={busy} onClick={() => void continueReturn(true)}>Continue to site</button>}
    {!mobile && <button disabled={busy} onClick={() => void continueReturn()}>{busy ? 'Confirming…' : popup ? (attempted ? 'Send result again' : 'Send result') : 'Return to site'}</button>}
  </main>;
}
